class CartDrawer extends withAnimation(HTMLElement) {
  constructor() {
    super();

    this.isOpen = false;
    this.scrollLockEnable = true;
    this.initialized = false;
    this.lastAddedProductId = null;
    this.lockedComplementaryProductId = null;
    this.hasLoadedLockedComplementary = false;
    this.complementaryLoadRequestId = 0;
    this.lockedComplementaryContent = '';
    this.skipNextComplementaryRerender = false;
    this.isInnerPanelOpen = false;
    this.restoreFrozenRaf = null;
    this.complementaryAbortController = null;

    this.handleResize = this.handleResize.bind(this);

    this.scheduleIdleInit();
  }

  scheduleIdleInit() {
    const init = () => this.initComponent();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 1500 });
    } else {
      setTimeout(init, 0);
    }
  }

  initComponent() {
    if (this.initialized) return;
    this.initialized = true;

    this.initElements();
    this.setHeaderCartIconAccessibility();

    this.setupEventListeners();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.queueRestoreFrozenComplementary();
    });

    DrawerManager.register(this, { side: 'right' });

    this.initAnimation({
      duration: 0.3
    });
  }

  initElements() {
    this.cartDrawer = this.querySelector('.drawer__wrapper');
    this.backDrop = this.querySelector('.modal__backdrop');
    this.scrollTracks = this.querySelectorAll('custom-scrollbar');
    this.customCursor = this.querySelector('custom-cursor');
  }

  reinitElements() {
    this.initElements();
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.backDrop) {
      this.backDrop.addEventListener('click', this.close.bind(this));
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      cartLink.blur();
      if (this.isOpen) {
        this.close();
      } else {
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  updateContentHeight() {
    const contentsWrapper = this.querySelector('.drawer__contents-wrapper');
    if (contentsWrapper) {
      this.style.setProperty('--content-height', `${contentsWrapper.offsetHeight}px`);
    }
  }

  handleResize() {
    if (this.isOpen) {
      this.updateContentHeight();
    }
  }

  async open(triggeredBy) {
    if (!this.initialized) {
      this.initComponent();
    }

    if (this.isOpen) return;
    await this.closeOtherPopup();

    this.customCursor?.disable?.();;

    this.isOpen = true;
    if (triggeredBy) {
      this.classList.add('opened-by-icon');
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('active');
    document.body.classList.add('has-cart-opened');

    const isMobile = DeviceUtils.isMobile;
    if (isMobile) {
      this.scrollLockEnable && scrollLock.lock();
      requestAnimationFrame(() => {
        DeviceUtils.updateHeaderSizes();
      });
    }
    DrawerManager.setActiveWithSecondary(this);
    if (DrawerManager.diffrentSide()) {
      DrawerManager.clearSecondaryIfOpen(this);
    }
    document.body.classList.add('overflow-hidden');

    this.updateContentHeight();

    const slider = this.querySelector('slider-component');
    if (slider) slider.reInit();

    this.progressBar = this.querySelector('progress-bar');
    this.progressBar && this.progressBar.resetProgress();

    window.addEventListener('resize', this.handleResize);

    requestAnimationFrame(() => {
      this.animateShowContent(isMobile);
    });
  }

  close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.isOpen = false;
    this.abortComplementaryRequest();
    window.removeEventListener('resize', this.handleResize);
    requestAnimationFrame(() => {
      this.animateHideContent();
    });
  }

  closeInnerPanel() {
    const drawerCompared = this.querySelector('drawer-compared');
    if (drawerCompared) {
      drawerCompared.classList.remove('is-open');
    }
    this.isInnerPanelOpen = false;
  }

  closeExtraPanel() {
    const extraPanel = this.querySelector('#CartChildPanel');
    if (extraPanel && extraPanel.classList.contains('is-visible')) {
      this.animateSlideOut(extraPanel, {
        slideDirection: 'left',
        onComplete: () => {
          extraPanel.classList.remove('is-visible');
          extraPanel.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }

  disconnectedCallback() {
    this.abortComplementaryRequest();
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
    if (this.restoreFrozenRaf) {
      cancelAnimationFrame(this.restoreFrozenRaf);
      this.restoreFrozenRaf = null;
    }
  }

  queueRestoreFrozenComplementary() {
    if (this.restoreFrozenRaf) {
      cancelAnimationFrame(this.restoreFrozenRaf);
      this.restoreFrozenRaf = null;
    }

    this.restoreFrozenRaf = requestAnimationFrame(() => {
      this.restoreFrozenRaf = requestAnimationFrame(() => {
        this.restoreFrozenRaf = null;
        this.restoreFrozenComplementaryAfterCartUpdate();
      });
    });
  }

  restoreFrozenComplementaryAfterCartUpdate() {
    if (!this.isOpen || !this.lockedComplementaryContent) return;

    this.updateComplementaryTargets(this.lockedComplementaryContent);

    if (DeviceUtils.isMobile && this.isInnerPanelOpen) {
      const drawerCompared = this.querySelector('drawer-compared');
      if (drawerCompared) {
        drawerCompared.classList.add('is-open');
      }
    }
  }

  getAddedProductId(parsedState) {
    if (!parsedState) return null;

    if (parsedState.product_id) return String(parsedState.product_id);
    if (parsedState.product?.id) return String(parsedState.product.id);

    if (Array.isArray(parsedState.items) && parsedState.items.length > 0) {
      const lastItem = parsedState.items[parsedState.items.length - 1];
      if (lastItem?.product_id) return String(lastItem.product_id);
    }

    return null;
  }

  getComplementaryTargets() {
    return {
      desktop: this.querySelector('#CartChildPanel-Scroll'),
      mobile: this.querySelector('drawer-compared .drawer-compared__content')
    };
  }

  getMobileComplementaryContent(content = '') {
    if (!content) return '';

    const wrapper = document.createElement('div');
    wrapper.innerHTML = content;
    wrapper.querySelectorAll('.cart-complementary-list__spacer').forEach((spacer) => spacer.remove());
    wrapper.querySelectorAll('product-card').forEach((card, index) => {
      if (index >= 2) card.remove();
    });
    return wrapper.innerHTML;
  }

  updateComplementaryTargets(content = '') {
    const { desktop, mobile } = this.getComplementaryTargets();
    const desktopHtml = content || '<span class="cart-complementary-list__spacer"></span>';
    const mobileHtml = this.getMobileComplementaryContent(content);

    if (desktop) desktop.innerHTML = desktopHtml;
    if (mobile) mobile.innerHTML = mobileHtml;
  }

  abortComplementaryRequest() {
    if (!this.complementaryAbortController) return;
    this.complementaryAbortController.abort();
    this.complementaryAbortController = null;
  }

  async loadLockedComplementaryProducts() {
    if (!this.lockedComplementaryProductId) return false;

    const recommendationsUrl = this.dataset.complementaryUrl;
    if (!recommendationsUrl) return false;

    this.abortComplementaryRequest();
    const abortController = new AbortController();
    this.complementaryAbortController = abortController;

    const requestUrl = `${recommendationsUrl}&product_id=${encodeURIComponent(this.lockedComplementaryProductId)}`;
    let content = '';
    try {
      const response = await fetch(requestUrl, {
        cache: 'no-store',
        signal: abortController.signal
      });
      const htmlText = await response.text();
      if (abortController.signal.aborted || !this.isOpen) return false;
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      content = html.querySelector('[data-cart-drawer-complementary-source]')?.innerHTML?.trim() || '';
    } catch (error) {
      if (error?.name === 'AbortError') return false;
      console.error('[CartDrawer] complementary fetch error:', error);
      content = '';
    } finally {
      if (this.complementaryAbortController === abortController) {
        this.complementaryAbortController = null;
      }
    }

    if (abortController.signal.aborted || !this.isOpen) return false;
    this.updateComplementaryTargets(content);
    this.lockedComplementaryContent = content;
    this.hasLoadedLockedComplementary = true;
    return Boolean(content);
  }

  async showAdditionalPanelAfterComplementaryLoad(isMobile) {
    const requestId = ++this.complementaryLoadRequestId;

    this.closeInnerPanel();
    const extraPanel = this.querySelector('#CartChildPanel');
    if (extraPanel) {
      extraPanel.classList.remove('is-visible');
      extraPanel.setAttribute('aria-hidden', 'true');
    }

    const hasComplementary = await this.loadLockedComplementaryProducts();
    if (!hasComplementary) return;
    if (!this.isOpen || requestId !== this.complementaryLoadRequestId) return;

    if (isMobile) {
      const drawerCompared = this.querySelector('drawer-compared');
      if (drawerCompared) {
        drawerCompared.classList.add('is-open');
        this.isInnerPanelOpen = true;
      }
      return;
    }

    if (!extraPanel) return;
    extraPanel.setAttribute('aria-hidden', 'false');
    this.animateSlideIn(extraPanel, {
      slideDirection: 'right',
      onComplete: () => {
        extraPanel.classList.add('is-visible');
        const extraPanelScrolls = extraPanel.querySelectorAll('custom-scrollbar');
        extraPanelScrolls.forEach((scroll) => scroll.updateThumbPosition?.());
      }
    });
  }

  onShowActions(isMobile) {
    this.progressBar && this.progressBar.popupOpen();
    if (isMobile) {
      DrawerManager.clearSecondaryIfOpen(this);
    }
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
    this.scrollTracks && this.scrollTracks.forEach((scroll) => scroll.updateThumbPosition?.());
    this.customCursor?.enable?.();

    if (this.openedByAddToCart) {
      this.openedByAddToCart = false;
      if (!this.lockedComplementaryProductId) {
        this.lockedComplementaryProductId = this.lastAddedProductId;
        this.hasLoadedLockedComplementary = false;
      }

      this.showAdditionalPanelAfterComplementaryLoad(isMobile);
    }
  }

  onCloseActions() {
    this.isClosing = false;
    this.abortComplementaryRequest();
    this.complementaryLoadRequestId += 1;
    this.lockedComplementaryProductId = null;
    this.lockedComplementaryContent = '';
    this.hasLoadedLockedComplementary = false;
    this.isInnerPanelOpen = false;
    this.closeInnerPanel();

    const extraPanel = this.querySelector('#CartChildPanel');
    if (extraPanel) {
      extraPanel.classList.remove('is-visible');
      extraPanel.setAttribute('aria-hidden', 'true');
    }

    removeTrapFocus(this.activeElement);
    this.classList.remove('active', 'opened-by-icon', 'has-animated-progress');
    document.body.classList.remove('has-cart-opened');
    this.customCursor?.disable?.();;
    DrawerManager.unsetActive(this);
    if (!window.DeviceUtils.isMobile) {
      document.body.classList.remove('overflow-hidden');
    }
    const cartDrawerScrolls = this.querySelectorAll('.drawer__contents-wrapper-scroll, .drawer__contents-scroll');
    cartDrawerScrolls.forEach((scroll) => {
      scroll.scrollTop = 0;
    });
  }

  forceClose() {
    this.classList.remove('active');
    document.body.classList.remove('has-cart-opened');
    this.isOpen = false;
    window.removeEventListener('resize', this.handleResize);
    this.onCloseActions();
  }

  async animateShowContent(isMobile) {
    await this.animateSlideIn(this.cartDrawer, {
      slideDirection: 'right',
      staggerSelector: isMobile ? false : '[data-stagger]',
      backDrop: this.backDrop,
      onComplete: () => this.onShowActions(isMobile)
    });
  }

  async animateHideContent() {
    const extraPanel = this.querySelector('#CartChildPanel');
    if (extraPanel && extraPanel.classList.contains('is-visible')) {
      extraPanel.classList.remove('is-visible');
      extraPanel.setAttribute('aria-hidden', 'true');

      const translateTo = this.isRTL ? '-50%' : '50%';

      await this.motion.animate(extraPanel, {
        transform: ['translate(0,0)', `translateX(${translateTo})`],
        opacity: [1, 0],
        visibility: ['visible', 'hidden']
      }, { duration: 0.2, easing: this.animationConfig.easing }).finished;
    }

    this.isAnimating = false;

    this.animateSlideOut(this.cartDrawer, {
      slideDirection: 'left',
      backDrop: this.backDrop,
      onComplete: () => this.onCloseActions()
    });
  }
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const keepComplementaryStatic = Boolean(
      this.skipNextComplementaryRerender && this.isOpen && this.lockedComplementaryProductId
    );

    if (!keepComplementaryStatic) {
      const addedProductId = this.getAddedProductId(parsedState);
      if (addedProductId) {
        this.lastAddedProductId = addedProductId;
        if (!this.isOpen) {
          this.lockedComplementaryProductId = addedProductId;
          this.hasLoadedLockedComplementary = false;
        }
      }
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.isOpen && this.lockedComplementaryProductId) {
      if (keepComplementaryStatic) {
        this.updateComplementaryTargets(this.lockedComplementaryContent);
        this.hasLoadedLockedComplementary = true;
      } else {
        this.hasLoadedLockedComplementary = false;
        this.loadLockedComplementaryProducts();
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.openedByAddToCart = !keepComplementaryStatic;
      this.skipNextComplementaryRerender = false;
      this.open();
    }, 200);
  }

  renderContentsFromHTML(sectionsHtml) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const htmlContent = sectionsHtml[section.id];
      if (!htmlContent) return;

      const parsedHtml = new DOMParser().parseFromString(htmlContent, 'text/html');
      const newContent = section.selector
        ? parsedHtml.querySelector(section.selector)
        : parsedHtml.querySelector(`#shopify-section-${section.id}`);

      if (!newContent) return;

      const currentElement = section.selector
        ? this.querySelector(section.selector) || document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (currentElement) {
        currentElement.innerHTML = newContent.innerHTML;
      }
    });
  }

  async closeOtherPopup() {
    const quickViewModal = document.querySelector('quickview-modal');
    if (quickViewModal.isOpen) {
      await quickViewModal.hide();
    }

    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  get isOpened() {
    return this.isOpen;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
